/**
 * InvoicesSummaryWidget — donut of invoices by status (sized by count). The
 * donut fills the widget (no legend, no scroll); slice detail is on hover.
 *
 * Slices: the canonical invoice statuses (Paid / Unpaid / Partially Paid /
 * Open / Pending) plus the derived OVER PAID bucket, which the BE carves out
 * of Paid so the donut is a clean partition. Zero-count slices are hidden, so
 * Pending (and any status with no invoices in the selected range) drops out.
 * When the range has no invoices at all, an empty-state message is shown.
 *
 * Colors are dashboard-specific brand colors (not from public.status).
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import {
  PieChartCard,
  RefreshButton,
  WidgetCard,
} from "components/dashboard";
import type { PieChartCardData } from "components/dashboard";
import { useInvoicesSummary, formatQueryError } from "../hooks";
import type { LayoutItem, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

// Status IDs from public.status WHERE type='invoice'.
const STATUS_META: Record<number, { label: string; color: string }> = {
  18: { label: "Paid", color: "34,197,94" }, // green-500
  33: { label: "Unpaid", color: "239,68,68" }, // red-500
  38: { label: "Partially Paid", color: "245,158,11" }, // amber-500
  17: { label: "Open", color: "139,92,246" }, // violet-500
  37: { label: "Pending", color: "107,114,128" }, // gray-500
};
const OVER_PAID_LABEL = "Over Paid";
const OVER_PAID_COLOR = "59,130,246"; // blue-500

interface Slice {
  key: string | number;
  label: string;
  count: number;
  amountCents: number;
  color: string;
}

export function InvoicesSummaryWidget({ widget, layout }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useInvoicesSummary();

  // Build slices from the status buckets + the derived Over Paid bucket,
  // keeping only those with at least one invoice (hides Pending etc.).
  const slices: Slice[] = React.useMemo(() => {
    if (!data) return [];
    const built: Slice[] = data.statusBuckets
      .filter((b) => b.count > 0 && STATUS_META[b.statusId])
      .map((b) => ({
        key: b.statusId,
        label: STATUS_META[b.statusId].label,
        count: b.count,
        amountCents: b.amountCents,
        color: STATUS_META[b.statusId].color,
      }));
    if (data.overPaid.count > 0) {
      built.push({
        key: "over_paid",
        label: OVER_PAID_LABEL,
        count: data.overPaid.count,
        amountCents: data.overPaid.amountCents,
        color: OVER_PAID_COLOR,
      });
    }
    return built;
  }, [data]);

  const totalCount = slices.reduce((sum, s) => sum + s.count, 0);

  const pieData: PieChartCardData[] = slices.map((s) => ({
    label: s.label,
    value: s.count,
    amountCents: s.amountCents,
    color: s.color,
  }));

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "invoices-summary"])}
        />
      }
    >
      {!isLoading && totalCount === 0 ? (
        <div className="flex items-center justify-center h-full text-xs text-gray-400 py-8 text-center">
          No invoices in this date range
        </div>
      ) : (
        // Center the donut and let it grow to the widget width (capped so it
        // never exceeds the card's height → no scrollbar). aspect-square gives
        // the chart a concrete height on mobile, where the row is content-sized.
        <div className="h-full flex items-center justify-center">
          <div className="w-full max-w-[240px] aspect-square">
            <PieChartCard
              data={pieData}
              loading={isLoading}
              height="100%"
              centerValue={totalCount}
              centerLabel={totalCount === 1 ? "invoice" : "invoices"}
            />
          </div>
        </div>
      )}
    </WidgetCard>
  );
}
